/**
 * The template gallery (spec 50 R8, R10).
 *
 * Full starting appearances — sections *and* theme — so a merchant never faces
 * a blank canvas. Distinct from the Theme tab's colour presets, which repaint an
 * existing design; these create one.
 *
 * Rendered from the `STORE_APPEARANCE_TEMPLATES` constant, so the gallery needs
 * no round trip and cannot show a template the server would then refuse.
 */
import { useMemo } from "react";
import { ExternalLink, Sparkles } from "lucide-react";
import { Button } from "@flint/ui";
import { STORE_APPEARANCE_TEMPLATES } from "@flint/api-contract/store-appearance-templates";
import { AppearanceThumbnail } from "./appearance-thumbnail.tsx";

export interface TemplateGalleryProps {
  /** Store identity the previews render with, so they look like *this* store. */
  storeName: string;
  canManage: boolean;
  /** Which template key is mid-flight, so only that card shows a pending state. */
  pendingKey: string | null;
  onPick: (templateKey: string) => void;
  /** Empty-state framing (the store has nothing yet) vs. "add another". */
  variant: "empty" | "additional";
}

export function TemplateGallery({
  storeName,
  canManage,
  pendingKey,
  onPick,
  variant,
}: TemplateGalleryProps) {
  // Sections are built once per render of the gallery, not per card paint: the
  // builders mint fresh uuids on every call, and re-running them on each render
  // would churn every React key in the thumbnails.
  const previews = useMemo(
    () =>
      STORE_APPEARANCE_TEMPLATES.map((t) => ({
        key: t.key,
        name: t.name,
        description: t.description,
        theme: t.theme,
        sections: t.buildSections({
          storeName,
          tagline: null,
          coverImageUrl: null,
          phone: null,
        }) as unknown[],
      })),
    [storeName],
  );

  return (
    <section aria-labelledby="template-gallery-heading">
      <div className="flex items-start gap-2">
        <Sparkles size={18} className="mt-0.5 shrink-0 text-accent" aria-hidden />
        <div>
          <h2 id="template-gallery-heading" className="text-base font-semibold text-ink">
            {variant === "empty" ? "Start with a template" : "Start another from a template"}
          </h2>
          <p className="mt-1 text-sm text-muted">
            {variant === "empty"
              ? "Pick a look to begin with. Everything stays editable afterwards — sections, colours, wording."
              : "Build a second look — a sale, a season — and switch to it whenever you like."}
          </p>
        </div>
      </div>

      {/* Four templates, one row at desktop; 2×2 below xl rather than overflow. */}
      <ul className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {previews.map((t) => (
          <li
            key={t.key}
            className="flex flex-col overflow-hidden rounded border border-line bg-paper shadow"
          >
            <AppearanceThumbnail
              sections={t.sections}
              theme={t.theme}
              className="h-40 border-b border-line"
            />
            <div className="flex flex-1 flex-col gap-1 p-3">
              <h3 className="text-sm font-semibold text-ink">{t.name}</h3>
              <p className="text-xs text-muted">{t.description}</p>
              {/* `mt-auto` pins the actions to the bottom of the card rather
                  than letting them follow the description. Grid items stretch
                  to the tallest in their row, so descriptions that wrap to
                  different line counts would otherwise leave the buttons on
                  ragged baselines across the row.

                  Stacked full-width rather than side by side: at four cards a
                  row the cards are narrow enough that "Use this template"
                  wrapped to two lines beside "Live preview" and the pair sat
                  lopsided. Stacking keeps every label on one line at any
                  width, and lets the primary action read as primary. */}
              <div className="mt-auto flex flex-col gap-2 pt-3">
                {/* A plain anchor, not a router Link: it opens a NEW TAB, so
                    client-side navigation buys nothing. Read-only, so it's
                    offered to view-only members too. */}
                <a
                  href={`/store/admin/storefront/templates/${t.key}`}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex h-8 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded border border-line bg-paper px-3 text-sm font-medium text-ink-2 transition hover:border-line-2 hover:bg-bg"
                >
                  <ExternalLink size={13} aria-hidden />
                  Live preview
                </a>
                {canManage ? (
                  <Button
                    intent="primary"
                    size="sm"
                    className="w-full whitespace-nowrap"
                    disabled={pendingKey !== null}
                    onClick={() => onPick(t.key)}
                  >
                    {pendingKey === t.key ? "Creating…" : "Use this template"}
                  </Button>
                ) : null}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
